import React, { FC, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  css,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
} from '@mui/material';
import { usePostWotcSurvey } from '@features/wotc/hooks/usePostWotcSurvey';
import { toast } from 'react-toastify';
import { LoadingButton } from '@mui/lab';
import { WotcSurveyData } from './types';
import { staticFileLocation } from '@server/constants';

type SsiFormModalProps = {
  ssiFormModal: boolean;
  wotcSurveyData: WotcSurveyData;
  handleClose: () => void;
};
const defaultValues = {
  ssi1: 'Y',
};
const SsiFormModal: FC<SsiFormModalProps> = ({
  ssiFormModal,
  handleClose,
  wotcSurveyData,
}) => {
  const [errorText, setErrorText] = useState('');
  const { sendWotcSurvey, isSendingWotcSurvey } = usePostWotcSurvey({
    setErrorText,
  });
  const { handleSubmit, control, setValue } = useForm({
    defaultValues,
  });

  // Function to handle form submission
  const onSubmit = (data: { ssi1: any }) => {
    const options = {
      onSuccess: (data: { status: number; message: string }) => {
        toast.success('Data submitted successfully');
        handleClose();
      },
    };

    const transformedData = {
      ...data,
      status: 8,
    };
    sendWotcSurvey({ ...transformedData }, options);
  };

  useEffect(() => {
    if (wotcSurveyData) {
      Object.keys(defaultValues).forEach((key) => {
        const typedKey = key as keyof typeof defaultValues &
          keyof WotcSurveyData;
        if (wotcSurveyData[typedKey] !== undefined) {
          if (wotcSurveyData[typedKey] !== null) {
            setValue(typedKey, wotcSurveyData[typedKey]);
          }
        }
      });
    }
  }, [wotcSurveyData, setValue]);

  return (
    <Dialog open={ssiFormModal} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', right: 8, top: 8 }}
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
        <br />
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          gap="10px"
        >
          <img
            src={`${staticFileLocation}img/images/icon9.png`}
            alt="Logo"
            css={css`
              max-height: 60px;
              display: block;
            `}
          />
          <Typography variant="h5">
            Supplemental Security Income (SSI)
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          {errorText && (
            <Alert sx={{ mb: 1 }} severity="error">
              {errorText}
            </Alert>
          )}
          {/* Question 1 */}
          <FormControl component="fieldset" fullWidth>
            <Box
              sx={{
                display: 'flex',
                alignItems: {
                  xs: 'start',
                  sm: 'center',
                },
                justifyContent: 'space-between',
                flexDirection: {
                  xs: 'column',
                  sm: 'row',
                },
                gap: '4px',
              }}
            >
              <FormLabel component="legend">
                Did you receive supplemental security income (SSI) benefits for
                any month ending during the past 60 days?
              </FormLabel>
              <Controller
                name="ssi1"
                control={control}
                rules={{ required: 'This field is required' }}
                render={({ field }) => (
                  <RadioGroup {...field} row sx={{ flexWrap: 'nowrap' }}>
                    <FormControlLabel
                      value="Y"
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="N"
                      control={<Radio />}
                      label="No"
                    />
                    <FormControlLabel
                      value="U"
                      control={<Radio />}
                      label="Unsure"
                    />
                  </RadioGroup>
                )}
              />
            </Box>
          </FormControl>

          {/* Submit Button */}
          <Box width="100%" textAlign="center">
            <LoadingButton
              loading={isSendingWotcSurvey}
              sx={{ width: '100%', mt: 3 }}
              type="submit"
              variant="contained"
              color="primary"
            >
              Submit
            </LoadingButton>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SsiFormModal;
